import React, { useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ContentLayout,
  Header,
  SpaceBetween,
  Box,
  Tabs,
  Spinner,
  Table,
  TableProps,
  Modal,
  FileUpload,
  Button as CloudscapeButton,
  ProgressBar,
} from '@cloudscape-design/components';
import Editor from '@monaco-editor/react';
import { Skill } from '@amzn/finspace-service-typescript-client';
import { Button } from '../common/Button';
import { useSkillViewStore } from '../../store/skill-view-store';
import { useTheme } from '../../providers';
import { Mode } from '@cloudscape-design/global-styles';
import { useExperimentalMode } from '../../hooks';
import { ChevronRight, ChevronDown, FileIcon, FolderIcon, NewFileIcon, NewFolderIcon, TrashIcon } from './FileExplorerIcons';
import './skill-view-page.scss';

const EXT_TO_MONACO: Record<string, string> = {
  '.py': 'python', '.json': 'json', '.md': 'markdown', '.yaml': 'yaml',
  '.yml': 'yaml', '.xml': 'xml', '.js': 'javascript', '.ts': 'typescript',
  '.html': 'html', '.css': 'css', '.sh': 'shell',
};

const getMonacoLanguage = (filePath: string): string => {
  const ext = filePath.substring(filePath.lastIndexOf('.')).toLowerCase();
  return EXT_TO_MONACO[ext] || 'plaintext';
};

// --- Tree structures ---

interface TreeNode {
  name: string;
  path: string;
  isDir: boolean;
  children: TreeNode[];
}

const buildTree = (paths: string[]): TreeNode[] => {
  const root: TreeNode[] = [];
  for (const path of paths) {
    const parts = path.replace(/\/$/, '').split('/');
    let current = root;
    let accumulated = '';
    for (let i = 0; i < parts.length; i++) {
      accumulated += (i > 0 ? '/' : '') + parts[i];
      const isLast = i === parts.length - 1;
      const isDir = !isLast || path.endsWith('/');
      let node = current.find(n => n.name === parts[i]);
      if (!node) {
        node = { name: parts[i], path: accumulated, isDir, children: [] };
        current.push(node);
      }
      current = node.children;
    }
  }
  return root;
};

// --- Inline input ---

const InlineInput: React.FC<{
  depth: number;
  isFolder: boolean;
  onSubmit: (value: string) => void;
  onCancel: () => void;
}> = ({ depth, isFolder, onSubmit, onCancel }) => {
  const ref = useRef<HTMLInputElement>(null);
  const [value, setValue] = React.useState('');

  useEffect(() => { ref.current?.focus(); }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && value.trim()) onSubmit(value.trim());
    else if (e.key === 'Escape') onCancel();
  };

  return (
    <div className="tree-node-inline-input" style={{ paddingLeft: `${depth * 16 + 6}px` }}>
      <span className="tree-node__icon">{isFolder ? <FolderIcon open={false} /> : <FileIcon />}</span>
      <input
        ref={ref}
        value={value}
        onChange={e => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => value.trim() ? onSubmit(value.trim()) : onCancel()}
      />
    </div>
  );
};

// --- File tree node ---

const FileTreeNode: React.FC<{
  node: TreeNode;
  depth: number;
  isDark: boolean;
}> = ({ node, depth, isDark }) => {
  const { selectedPath, expanded, creating, toggleExpand, selectPath, loadFile, setContextMenu, submitCreate, cancelCreate } = useSkillViewStore();
  const { skillId, versionId } = useParams<{ skillId: string; versionId: string }>();

  const isOpen = expanded.has(node.path);
  const isSelected = selectedPath === node.path;
  const showInlineInput = creating && creating.parentPath === node.path && node.isDir && isOpen;
  const selectedClass = isSelected ? (isDark ? 'tree-node--selected-dark' : 'tree-node--selected-light') : '';

  const handleClick = () => {
    if (node.isDir) {
      selectPath(node.path, true);
      toggleExpand(node.path);
    } else {
      const { newFiles } = useSkillViewStore.getState();
      if (newFiles.has(node.path)) {
        selectPath(node.path, false);
      } else if (skillId && versionId) {
        loadFile(skillId, versionId, node.path);
      }
    }
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, path: node.path, isDir: node.isDir });
  };

  return (
    <>
      <div
        className={`tree-node ${selectedClass}`}
        style={{ paddingLeft: `${depth * 16 + 6}px` }}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
      >
        <span className="tree-node__chevron">{node.isDir ? (isOpen ? <ChevronDown /> : <ChevronRight />) : ''}</span>
        <span className="tree-node__icon">{node.isDir ? <FolderIcon open={isOpen} /> : <FileIcon />}</span>
        <span className="tree-node__name">{node.name}</span>
      </div>
      {node.isDir && isOpen && (
        <>
          {showInlineInput && (
            <InlineInput
              depth={depth + 1}
              isFolder={creating!.type === 'folder'}
              onSubmit={submitCreate}
              onCancel={cancelCreate}
            />
          )}
          {node.children.map(child => (
            <FileTreeNode key={child.path} node={child} depth={depth + 1} isDark={isDark} />
          ))}
        </>
      )}
    </>
  );
};

// --- Main page ---

const SkillViewPage: React.FC = () => {
  const { skillId, versionId } = useParams<{ skillId: string; versionId: string }>();
  const navigate = useNavigate();
  const { mode } = useTheme();
  const isDark = mode === Mode.Dark;
  const { experimentalModeEnabled } = useExperimentalMode();

  const {
    skill, isLoading, selectedPath, isSelectedDir, fileContent, isFileLoading,
    expanded, allPathsOverride, creating, contextMenu,
    versions, isVersionsLoading,
    uploadModalVisible, uploadFile, isUploadLoading, isUploading, uploadProgress,
    fetchSkill, startCreate, deletePath, setContextMenu, setFileContent, fetchVersions,
    openUploadModal, closeUploadModal, setUploadFile, uploadNewVersion, reset,
  } = useSkillViewStore();

  useEffect(() => {
    if (skillId && versionId) fetchSkill(skillId, versionId);
    return () => reset();
  }, [skillId, versionId]);

  const basePaths = useMemo(() => skill?.fileStructure?.split('\n').filter(Boolean) || [], [skill]);
  const allPaths = allPathsOverride.length > 0 ? allPathsOverride : basePaths;
  const tree = useMemo(() => buildTree(allPaths), [allPaths]);

  // Close context menu on click
  useEffect(() => {
    if (contextMenu) {
      const handler = () => setContextMenu(null);
      window.addEventListener('click', handler);
      return () => window.removeEventListener('click', handler);
    }
  }, [contextMenu]);

  if (isLoading) {
    return (
      <ContentLayout defaultPadding>
        <Box textAlign="center" padding="xxl"><Spinner size="large" /></Box>
      </ContentLayout>
    );
  }

  const themeClass = isDark ? 'dark' : 'light';

  return (
    <ContentLayout
      defaultPadding
      header={
        <Header
          variant="h1"
          actions={
            <CloudscapeButton variant="primary" onClick={openUploadModal} loading={isUploadLoading}>
              Upload New Version
            </CloudscapeButton>
          }
        >
          {skill?.name || 'Skill'}
        </Header>
      }
    >
      <SpaceBetween direction="vertical" size="l">
        <Tabs
          onChange={({ detail }) => {
            if (detail.activeTabId === 'versions' && skillId) fetchVersions(skillId, experimentalModeEnabled);
          }}
          tabs={[
          {
            id: 'details',
            label: 'Details',
            content: (
              <div className="skill-view">
                {/* Sidebar */}
                <div className={`skill-view__sidebar skill-view__sidebar--${themeClass}`}>
                  <div className="skill-view__sidebar-header">
                    Explorer
                    <div className="skill-view__sidebar-actions">
                      <span
                        title="New File"
                        className={`skill-view__sidebar-action ${!isSelectedDir ? 'skill-view__sidebar-action--disabled' : ''}`}
                        onClick={() => isSelectedDir && selectedPath && startCreate('file', selectedPath)}
                      ><NewFileIcon /></span>
                      <span
                        title="New Folder"
                        className={`skill-view__sidebar-action ${!isSelectedDir ? 'skill-view__sidebar-action--disabled' : ''}`}
                        onClick={() => isSelectedDir && selectedPath && startCreate('folder', selectedPath)}
                      ><NewFolderIcon /></span>
                      <span
                        title="Delete"
                        className={`skill-view__sidebar-action ${!selectedPath ? 'skill-view__sidebar-action--disabled' : ''}`}
                        onClick={() => selectedPath && deletePath()}
                      ><TrashIcon /></span>
                    </div>
                  </div>
                  <div className="skill-view__sidebar-tree">
                    {tree.map(node => (
                      <FileTreeNode key={node.path} node={node} depth={0} isDark={isDark} />
                    ))}
                  </div>
                </div>

                {/* Editor */}
                <div className={`skill-view__editor-area skill-view__editor-area--${themeClass}`}>
                  {selectedPath && !isSelectedDir && (
                    <div className={`skill-view__tab-bar skill-view__tab-bar--${themeClass}`}>
                      <span><FileIcon /></span>
                      <span>{selectedPath.split('/').pop()}</span>
                    </div>
                  )}
                  <div className="skill-view__editor-content">
                    {isFileLoading ? (
                      <Box textAlign="center" padding="xxl"><Spinner /></Box>
                    ) : selectedPath && !isSelectedDir ? (
                      <Editor
                        height="100%"
                        language={getMonacoLanguage(selectedPath)}
                        value={fileContent}
                        onChange={(value) => setFileContent(value || '')}
                        theme={isDark ? 'vs-dark' : 'light'}
                        options={{
                          minimap: { enabled: true },
                          fontSize: 13,
                          lineNumbers: 'on',
                          wordWrap: 'on',
                          scrollBeyondLastLine: false,
                          automaticLayout: true,
                          tabSize: 2,
                        }}
                      />
                    ) : (
                      <div className={`skill-view__empty-state skill-view__empty-state--${themeClass}`}>
                        Select a file to start editing
                      </div>
                    )}
                  </div>
                </div>

                {/* Context menu */}
                {contextMenu && (
                  <div className={`context-menu context-menu--${themeClass}`} style={{ top: contextMenu.y, left: contextMenu.x }}>
                    {contextMenu.isDir && (
                      <>
                        <div className="context-menu__item" onClick={() => startCreate('file', contextMenu.path)}>New File</div>
                        <div className="context-menu__item" onClick={() => startCreate('folder', contextMenu.path)}>New Folder</div>
                        <div className="context-menu__divider" />
                      </>
                    )}
                    <div
                      className={`context-menu__item ${isDark ? 'context-menu__item--danger-dark' : 'context-menu__item--danger'}`}
                      onClick={() => deletePath(contextMenu.path, contextMenu.isDir)}
                    >Delete</div>
                  </div>
                )}
              </div>
            ),
          },
          {
            id: 'versions',
            label: 'Version History',
            content: (
              <Table
                columnDefinitions={[
                  { id: 'description', header: <Box fontWeight="bold">Description</Box>, cell: (v: Skill) => v.description || '-', width: '40%' },
                  { id: 'createdAt', header: <Box fontWeight="bold">Created At</Box>, cell: (v: Skill) => v.createdAt || '-', width: '20%' },
                  { id: 'createdBy', header: <Box fontWeight="bold">Created By</Box>, cell: (v: Skill) => v.createdBy || '-', width: '20%' },
                  {
                    id: 'actions', header: <Box fontWeight="bold">Actions</Box>, width: '20%',
                    cell: (v: Skill) => (
                      <Button variant="secondary" href={`/skill/${v.skillId}/version/${v.versionId}`} disabled={v.active === 'true'}>
                        {v.active === 'true' ? 'Active' : 'Restore'}
                      </Button>
                    ),
                  },
                ]}
                items={versions}
                loading={isVersionsLoading}
                loadingText="Loading versions..."
                empty={<Box textAlign="center">No previous versions found</Box>}
                variant="borderless"
              />
            ),
          },
        ]} />
      </SpaceBetween>

      <Modal
        visible={uploadModalVisible}
        onDismiss={closeUploadModal}
        header="Upload New Version"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <CloudscapeButton variant="link" onClick={closeUploadModal} disabled={isUploading}>
                Cancel
              </CloudscapeButton>
              <CloudscapeButton
                variant="primary"
                disabled={uploadFile.length === 0 || isUploading}
                onClick={async () => {
                  if (!skillId) return;
                  const newSkill = await uploadNewVersion(skillId);
                  if (newSkill?.skillId && newSkill?.versionId) {
                    navigate(`/skill/${newSkill.skillId}/version/${newSkill.versionId}`);
                  }
                }}
              >
                {isUploading ? <Spinner size="normal" /> : 'Upload'}
              </CloudscapeButton>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <FileUpload
            accept=".zip,application/zip"
            constraintText="Only ZIP files are allowed"
            value={uploadFile}
            onChange={({ detail }) => setUploadFile(detail.value)}
            i18nStrings={{
              uploadButtonText: (multiple) => multiple ? 'Choose files' : 'Choose file',
              dropzoneText: (multiple) => multiple ? 'Drop files to upload' : 'Drop file to upload',
              removeFileAriaLabel: (fileIndex) => `Remove file ${fileIndex + 1}`,
              limitShowFewer: 'Show fewer files',
              limitShowMore: 'Show more files',
              errorIconAriaLabel: 'Error',
            }}
          />
          {isUploading && (
            <ProgressBar
              value={uploadProgress}
              label="Upload progress"
              description={`${Math.round(uploadProgress)}% complete`}
            />
          )}
        </SpaceBetween>
      </Modal>
    </ContentLayout>
  );
};

export default SkillViewPage;
